import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  booleanAttribute,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LUCIDE_ICONS } from '@compsych-ui-components/shared/assets/lucide-icons.generated';

/** Visual style of a `compsych-button`. Controls color, border, and elevation. */
export type ButtonVariant =
  | 'filled'
  | 'tonal'
  | 'outlined'
  | 'elevated'
  | 'text'
  | 'danger'
  | 'danger-outlined';

/** Button dimensions — controls height, padding, font size, and icon size. */
export type ButtonSize = 'small' | 'medium' | 'large' | 'xlarge';

/** Native button behaviour. `'submit'`/`'reset'` make the component a real form control. */
export type ButtonType = 'button' | 'submit' | 'reset';

/** Icon pixel size per button size (per design: sm/md 16, lg 20, xl 24). */
const ICON_SIZE_BY_BUTTON_SIZE: Record<ButtonSize, number> = {
  small: 16,
  medium: 16,
  large: 20,
  xlarge: 24,
};

/**
 * A themeable button that renders a native `<button>` element, so it is fully
 * form-submittable and keyboard/screen-reader accessible out of the box. Icons
 * are Lucide glyphs (matching the rest of the library); a button may carry a
 * leading icon, a trailing icon, or both.
 */
@Component({
  selector: 'compsych-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      [class]="buttonClasses"
      [type]="type"
      [disabled]="disabled || loading"
      [attr.aria-busy]="loading ? 'true' : null"
      [attr.aria-label]="computedAriaLabel"
      (pointerdown)="onPointerDown($event)"
      (keydown)="onKeyDown($event)"
    >
      @if (loading) {
        <span class="icon spinner" aria-hidden="true"></span>
      } @else if (leadingIcon) {
        <span class="icon" aria-hidden="true" [innerHTML]="leadingIconSvg"></span>
      }
      @if (!iconOnly) {
        <span class="label">{{ label }}<ng-content /></span>
      }
      @if (trailingIcon && !iconOnly) {
        <span class="icon" aria-hidden="true" [innerHTML]="trailingIconSvg"></span>
      }
    </button>
  `,
  styleUrl: './button.css',
})
export class ButtonComponent implements OnChanges {
  private readonly sanitizer = inject(DomSanitizer);

  /** Text label rendered on the button. */
  @Input() label = '';
  /** Visual style of the button. Defaults to `'filled'`. */
  @Input() variant: ButtonVariant = 'filled';
  /** Button dimensions — controls height, padding, font size, and icon size. Defaults to `'small'`. */
  @Input() size: ButtonSize = 'small';
  /** Lucide icon name shown before the label (e.g. `'house'`). Swapped for a spinner when loading. */
  @Input() leadingIcon?: string;
  /** Lucide icon name shown after the label (e.g. `'chevron-right'`). Can be combined with `leadingIcon`. */
  @Input() trailingIcon?: string;
  /** Native button type. Use `'submit'` inside a `<form>` to submit it. Defaults to `'button'`. */
  @Input() type: ButtonType = 'button';
  /**
   * Accessible name. Required for icon-only buttons since the icon is decorative.
   * When `iconOnly` or `loading` is set and this is omitted, `label` is used.
   */
  @Input() ariaLabel?: string;

  /** Hides the label and renders a square icon-only button. Uses `leadingIcon`. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) iconOnly = false;
  /** Disables the button — non-interactive and rendered in a neutral grey. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) disabled = false;
  /**
   * Loading state. Swaps the leading icon for a spinner and suppresses activation
   * (native `disabled` blocks click/keyboard), while keeping the label and exposing
   * `aria-busy="true"`. Retains the variant's colours. Supports boolean coercion.
   */
  @Input({ transform: booleanAttribute }) loading = false;
  /** Stretches the button to the full width of its container. Supports boolean coercion. */
  @Input({ transform: booleanAttribute }) fullWidth = false;

  /** Mirrors `fullWidth` onto the host so it can stretch. */
  @HostBinding('class.full-width') get hostFullWidth(): boolean {
    return this.fullWidth;
  }

  /**
   * Marks the host non-interactive while disabled or loading. The inner `<button>` sets
   * `pointer-events: none` when disabled, so without this a click lands on the host wrapper
   * instead — firing any `(click)` a consumer bound on `<compsych-button>`. Blocking pointer
   * events at the host too makes the disabled state honour its "non-interactive" contract.
   */
  @HostBinding('class.non-interactive') get hostNonInteractive(): boolean {
    return this.disabled || this.loading;
  }

  /** Pre-sanitized icon markup, recomputed on input change (avoids re-sanitizing every CD cycle). */
  protected leadingIconSvg: SafeHtml | null = null;
  protected trailingIconSvg: SafeHtml | null = null;

  ngOnChanges(): void {
    this.leadingIconSvg = this.leadingIcon ? this.buildIconSvg(this.leadingIcon) : null;
    this.trailingIconSvg = this.trailingIcon ? this.buildIconSvg(this.trailingIcon) : null;
  }

  /** Space-separated class list applied to the inner native button. */
  get buttonClasses(): string {
    return [
      'cmp-btn',
      this.variant,
      this.size,
      this.iconOnly ? 'icon-only' : '',
      this.loading ? 'loading' : '',
      this.fullWidth ? 'full-width' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  /** Resolves the accessible name. Icon-only buttons have no visible text, so they fall back to `label`. */
  get computedAriaLabel(): string | null {
    if (this.ariaLabel) {
      return this.ariaLabel;
    }
    // Loading keeps the visible label, so only icon-only needs an explicit name here.
    return this.iconOnly ? this.label || null : null;
  }

  /**
   * Looks up a Lucide icon by name and injects an explicit width/height so the SVG
   * renders at the correct size when injected via `[innerHTML]` (Angular's CSS
   * encapsulation does not reach dynamically injected content).
   */
  private buildIconSvg(name: string): SafeHtml {
    const size = ICON_SIZE_BY_BUTTON_SIZE[this.size];
    const svg = (LUCIDE_ICONS[name] ?? '').replace('<svg ', `<svg width="${size}" height="${size}" `);
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  /** M3 pressed ripple: pointer-down spawns a ripple at the pointer position. */
  onPointerDown(event: PointerEvent): void {
    if (this.disabled || this.loading || event.button !== 0) {
      return;
    }
    const host = event.currentTarget as HTMLElement;
    const rect = host.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    this.spawnRipple(host, x, y, this.farthestCornerDiameter(rect, x, y));
  }

  /** M3 pressed ripple: keyboard activation (Enter/Space) spawns a centered ripple. */
  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled || this.loading || event.repeat) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    const host = event.currentTarget as HTMLElement;
    const rect = host.getBoundingClientRect();
    this.spawnRipple(host, rect.width / 2, rect.height / 2, Math.hypot(rect.width, rect.height));
  }

  /** Diameter that reaches the farthest corner from (x, y), so the ripple always covers the button. */
  private farthestCornerDiameter(rect: DOMRect, x: number, y: number): number {
    const corners: Array<[number, number]> = [
      [0, 0],
      [rect.width, 0],
      [0, rect.height],
      [rect.width, rect.height],
    ];
    const maxRadius = Math.max(...corners.map(([cx, cy]) => Math.hypot(cx - x, cy - y)));
    return maxRadius * 2;
  }

  /**
   * Spawns an expanding ripple circle. Created imperatively with inline styles and
   * animated via the Web Animations API — Angular's emulated encapsulation does not
   * reach dynamically created nodes, so it cannot rely on the component stylesheet.
   */
  private spawnRipple(host: HTMLElement, x: number, y: number, diameter: number): void {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    const ripple = document.createElement('span');
    Object.assign(ripple.style, {
      position: 'absolute',
      borderRadius: '50%',
      backgroundColor: 'currentColor',
      pointerEvents: 'none',
      width: `${diameter}px`,
      height: `${diameter}px`,
      left: `${x - diameter / 2}px`,
      top: `${y - diameter / 2}px`,
    } as Partial<CSSStyleDeclaration>);
    host.appendChild(ripple);
    const animation = ripple.animate(
      [
        { transform: 'scale(0)', opacity: '0.16' },
        { transform: 'scale(1)', opacity: '0' },
      ],
      { duration: 450, easing: 'ease-out' },
    );
    animation.onfinish = () => ripple.remove();
  }
}
